/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure snapping for dragging elements on the stage: the moving selection's edges and centre
 * snap to other shapes' edges and centres and to the artboard, within a pixel radius.
 */

export interface ScreenBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface SnapResult {
  /** Extra movement to add so the selection lands on the snap line. */
  dx: number;
  dy: number;
  /** Screen x positions of vertical guide lines, and y positions of horizontal ones. */
  guidesX: number[];
  guidesY: number[];
}

export const SNAP_RADIUS = 6;

/** The three snap lines of a box along one axis: start, centre, end. */
function lines(start: number, end: number): number[] {
  return [start, (start + end) / 2, end];
}

/** Finds the smallest adjustment that aligns any moving line with any target line. */
function snapAxis(
  moving: number[],
  targets: number[],
  radius: number,
): { shift: number; guide: number } | undefined {
  let best: { shift: number; guide: number } | undefined;
  for (const m of moving) {
    for (const t of targets) {
      const shift = t - m;
      if (Math.abs(shift) <= radius && (!best || Math.abs(shift) < Math.abs(best.shift))) {
        best = { shift, guide: t };
      }
    }
  }
  return best;
}

/** Snaps a moving box to the targets' edges and centres. */
export function snapBox(
  moving: ScreenBox,
  targets: readonly ScreenBox[],
  radius = SNAP_RADIUS,
): SnapResult {
  const x = snapAxis(
    lines(moving.left, moving.right),
    targets.flatMap((t) => lines(t.left, t.right)),
    radius,
  );
  const y = snapAxis(
    lines(moving.top, moving.bottom),
    targets.flatMap((t) => lines(t.top, t.bottom)),
    radius,
  );
  return {
    dx: x?.shift ?? 0,
    dy: y?.shift ?? 0,
    guidesX: x ? [x.guide] : [],
    guidesY: y ? [y.guide] : [],
  };
}

/** Restricts a movement to the axis with the larger distance (the Shift constraint). */
export function constrainToAxis(dx: number, dy: number): { dx: number; dy: number } {
  return Math.abs(dx) >= Math.abs(dy) ? { dx, dy: 0 } : { dx: 0, dy };
}
